import { useMemo } from "react";
import type { PresetRef } from "../contract/types";
import { COURSE_SCHEDULE, FALLBACK_EVALUATION, FALLBACK_OUTLINE } from "../course/outline";
import { Blocks } from "./Blocks";
import { PresetShell } from "./PresetShell";
import {
  assessmentComponents,
  compactBlocks,
  createAtom,
  readNumber,
  readObjects,
  readRecord,
  readString,
  readStringArray,
  type PresetData,
} from "./data";
import type { BlockEventSink } from "../blocks/types";

/**
 * Panorama del curso: qué se ve, cómo se organiza y cómo se evalúa.
 *
 * Si `data` llega incompleto se cae al sílabo embebido (`FALLBACK_OUTLINE`) en
 * lugar de pintar una vista vacía: es preferible mostrar el curso real sin los
 * detalles que el agente olvidó, que una pantalla en blanco.
 */

export interface PresetViewProps {
  preset: PresetRef;
  data: PresetData;
  emit: BlockEventSink;
  interactive: boolean;
}

function moduleNumber(value: string): number {
  const digits = value.replace(/[^0-9]/g, "");
  return digits === "" ? 0 : Number(digits);
}

function unitStatus(modules: string[]): "done" | "current" | "upcoming" {
  if (modules.length === 0) return "upcoming";
  const statuses = modules.map((module) => {
    const number = moduleNumber(module);
    const week = COURSE_SCHEDULE.find((item) => moduleNumber(item.duration) === number);
    return week?.status ?? "upcoming";
  });
  if (statuses.every((status) => status === "done")) return "done";
  if (statuses.includes("current") || statuses.includes("done")) return "current";
  return "upcoming";
}

export function CourseOverview({ preset, data, emit, interactive }: PresetViewProps) {
  const program = readString(data, "program") ?? "UPC Escuela de Postgrado";
  const modality = readString(data, "modality") ?? "BLENDED · aula invertida";
  const weeks = readNumber(data, "weeks") ?? 8;
  const textbook = readString(data, "textbook");
  const assessment = readRecord(data, "assessment");

  const units = useMemo(() => {
    const declared = readObjects(data, "units");
    if (declared.length === 0) {
      return FALLBACK_OUTLINE.units.map((unit) => ({
        code: `U${unit.unit}`,
        title: unit.title,
        description: unit.modules.map((module) => module.title).join(" · "),
        modules: unit.modules.map((module) => `M${module.module}`),
      }));
    }
    return declared.map((unit, index) => ({
      code: readString(unit, "code") ?? `U${index + 1}`,
      title: readString(unit, "title") ?? `Unidad ${index + 1}`,
      description: readString(unit, "description") ?? "",
      modules: readStringArray(unit, "modules"),
    }));
  }, [data]);

  const moduleCount = units.reduce((total, unit) => total + unit.modules.length, 0) || 8;

  const rubricComponents = useMemo(() => {
    const fromData = assessmentComponents(assessment);
    return fromData.length > 0 ? fromData : (FALLBACK_EVALUATION.components ?? []);
  }, [assessment]);

  const blocks = compactBlocks([
    createAtom("prose", {
      markdown: `Este curso se organiza en **${units.length} unidades** y **${moduleCount} módulos** a lo largo de ${weeks} semanas, en modalidad ${modality.toLowerCase()}. Cada unidad cierra con un control de lectura y las evaluaciones de avance caen al final de los módulos 2, 4 y 6.`,
    }),

    createAtom("metric_cards", {
      meta: { title: "El curso en cifras" },
      cards: [
        { label: "Duración", value: String(weeks), unit: "semanas", hint: "Un módulo por semana" },
        { label: "Unidades", value: String(units.length), hint: "Diagnóstico, capital de trabajo, evaluación" },
        { label: "Módulos", value: String(moduleCount), hint: "Del M1 al M8" },
        {
          label: "Peso del trabajo final",
          value: String(Math.round((rubricComponents.find((item) => item.code === "TF")?.weight ?? 0.35) * 100)),
          unit: "%",
          hint: "Presentación grupal en el M8",
        },
      ],
    }),

    createAtom("timeline", {
      meta: { title: "Las tres unidades", subtitle: "En orden de avance" },
      orientation: "vertical",
      items: units.map((unit) => ({
        label: unit.code,
        title: unit.title,
        description: unit.description || null,
        duration: `${unit.modules.length} ${unit.modules.length === 1 ? "módulo" : "módulos"}`,
        tags: unit.modules,
        status: unitStatus(unit.modules),
      })),
    }),

    rubricComponents.length > 0
      ? createAtom("rubric", {
          meta: { title: "Cómo se calcula la nota final" },
          total_label: "Nota final del curso",
          formula: "Nota final = 20 % CL + 45 % EA + 35 % TF",
          components: rubricComponents,
        })
      : null,

    createAtom("callout", {
      variant: "tip",
      title: "Cómo aprovechar el asistente",
      body: "Pídele el panorama de una unidad, un caso resuelto paso a paso, o un simulador para mover los supuestos. Los controles de lectura de Coursera se responden con los quizzes que aparecen al final de cada explicación.",
    }),

    textbook
      ? createAtom("prose", {
          meta: { title: "Bibliografía base" },
          markdown: textbook,
        })
      : null,
  ]);

  return (
    <PresetShell
      eyebrow={program}
      title={preset.title ?? "Modelos Financieros y Evaluación de Proyectos"}
      lead={`${modality} · ${weeks} semanas · ${units.length} unidades y ${moduleCount} módulos`}
      chips={[
        { icon: "clock", label: `${weeks} semanas` },
        { icon: "layers", label: `${units.length} unidades` },
        { icon: "target", label: "CL 20 % · EA 45 % · TF 35 %" },
      ]}
    >
      <Blocks blocks={blocks} emit={emit} interactive={interactive} />
    </PresetShell>
  );
}

export default CourseOverview;
